import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { posthog } from '../../lib/posthog.client';
import { Modal, Spin, Button, Drawer, message, Tooltip } from 'antd';
import { RobotOutlined, DatabaseOutlined, HddOutlined, ReloadOutlined, FullscreenOutlined, FullscreenExitOutlined, BarChartOutlined, BulbOutlined, DashboardOutlined } from '@ant-design/icons';
import reportSummaryIcon from '../../assets/reportsummary.png';
import execute from '../../hooks/execute';
import { protectedResources } from '../../config/msalConfig';
import { AutoSummaryPanel } from '../../components/AutoSummary';
import { useUser } from '../../components/UserContext';
import { extractVisualData } from '../../services/powerBIDataExtractor';
import { getCachedSummary } from '../../services/aiSummarizationService';
import TitleComponent from '../../components/Common/TitleComponent';

const ReportSummaryIcon = ({ size = 64 }) => (
    <img
        src={reportSummaryIcon}
        alt="AI Report Summary"
        style={{ width: size, height: size, display: 'block', objectFit: 'contain' }}
    />
);
// Session expiry is handled by execute.jsx, no need to import here

function Report() {
    const location = useLocation();
    const { embedUrl: initialEmbedUrl, accessToken: initialAccessToken, isEmbedToken: initialIsEmbedToken } = location.state || {};
    const { reportId, moduleShortName } = useParams();
    const { userDetails } = useUser();
    const navigate = useNavigate();


    // Part 7: Get full module name from userDetails for template selection
    // userDetails.Modules has both ShortName ("IM") and ModuleName ("Incident Management")
    const currentModule = userDetails?.Modules?.find(
        m => m.ShortName === moduleShortName?.toUpperCase()
    );
    const moduleName = currentModule?.ModuleName || null;

    // State for token management
    const [embedUrl, setEmbedUrl] = useState(initialEmbedUrl);
    const [accessToken, setAccessToken] = useState(initialAccessToken);
    const [isEmbedToken, setIsEmbedToken] = useState(initialIsEmbedToken);
    const [tokenExpiration, setTokenExpiration] = useState(null); // Track token expiration time
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isInitializing, setIsInitializing] = useState(!initialEmbedUrl || !initialAccessToken); // Track if we need to fetch initial data
    const [showSummaryPanel, setShowSummaryPanel] = useState(false); // AI Summary panel visibility
    const [hasCachedSummary, setHasCachedSummary] = useState(false); // Whether cached summary exists
    const [autoOpenDrawer, setAutoOpenDrawer] = useState(false); // Auto-open drawer with cached summary
    const [forceSkipCache, setForceSkipCache] = useState(false); // Force skip cache for regeneration
    const [drawerExpanded, setDrawerExpanded] = useState(false); // Drawer expand/collapse state
    const [showDashboard, setShowDashboard] = useState(false); // Show dashboard view
    React.useEffect(() => {
        const handleOpenSummary = () => {
            setAutoOpenDrawer(true);
            setShowSummaryPanel(true);
        };
        window.addEventListener('bic:open-ai-report-summary', handleOpenSummary);
        return () => {
            window.removeEventListener('bic:open-ai-report-summary', handleOpenSummary);
        };
    }, []);

    React.useEffect(() => {
        window.dispatchEvent(
            new CustomEvent('bic:report-summary-visibility', {
                detail: { open: showSummaryPanel }
            })
        );
        return () => {
            window.dispatchEvent(
                new CustomEvent('bic:report-summary-visibility', {
                    detail: { open: false }
                })
            );
        };
    }, [showSummaryPanel]);

    // Removed auto-show dashboard when drawer is expanded to prevent automatic regeneration.
    // Dashboard mode should be explicitly triggered via the toggle button in the drawer.
    const [isDumpingData, setIsDumpingData] = useState(false); // Visual data dump in progress
    const [isDumpingDataset, setIsDumpingDataset] = useState(false); // Dataset dump in progress
    const [datasetId, setDatasetId] = useState(null); // Store dataset ID for DAX queries
    const [currentReportName, setCurrentReportName] = useState(''); // Current report name for drawer title
    const [currentPageName, setCurrentPageName] = useState(''); // Current page/tab name for drawer title
    const reportRef = useRef(null);
    const cacheCheckRef = useRef(false); // Prevent duplicate cache checks
    const retryCountRef = useRef(0);
    const refreshTimerRef = useRef(null); // Timer for proactive token refresh
    const isRefreshingRef = useRef(false); // Synchronous concurrency control to prevent race conditions
    const MAX_RETRY_ATTEMPTS = 2;
    const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000; // Refresh 10 minutes before expiration

    /**
     * Check if user has access to the requested module
     * Returns: true (has access), false (no access), undefined (still loading)
     */
    const hasModuleAccess = () => {
        if (!userDetails || !userDetails.Modules) {
            return undefined; // Still loading user details
        }
        // Check if the moduleShortName from URL matches any of user's assigned modules
        return userDetails.Modules.some(module => module.ShortName === moduleShortName);
    };

    // Redirect to dashboard if user doesn't have access to the module
    useEffect(() => {
        const access = hasModuleAccess();
        if (access === false) {
            console.log(
                "Redirecting to dashboard - user does not have access to module:",
                moduleShortName,
                "User modules:",
                userDetails?.Modules?.map(m => m.ShortName)
            );
            window.location.href = "/dashboard";
        }
    }, [userDetails, moduleShortName, navigate]);

    /**
     * Fetch initial report data when location.state is missing
     * This handles page refresh and direct URL access scenarios
     */
    const fetchInitialReportData = async () => {
        const fetchStartTime = Date.now();

        try {
            setIsInitializing(true);

            console.log('┌─────────────────────────────────────────────────────────────┐');
            console.log('│ [PowerBI Init] Fetching Initial Report Data                │');
            console.log('└─────────────────────────────────────────────────────────────┘');
            console.log('[PowerBI Init] ReportID:', reportId);
            console.log('[PowerBI Init] Step 1: Calling /pbi/get-all-reports...');

            // Call getAllReports to get all reports including this one
            const response = await execute(
                'POST',
                `${protectedResources.api.baseEndPoint}/pbi/get-all-reports`,
                {}
            );

            const apiDuration = Date.now() - fetchStartTime;
            console.log(`[PowerBI Init] Step 1: API call completed (${apiDuration}ms)`);

            if (!response || !response.success || !response.data) {
                throw new Error('Failed to fetch reports: Invalid response');
            }

            console.log('[PowerBI Init] Step 2: Finding report in response...');
            console.log(`[PowerBI Init] Total reports in response: ${response.data.length}`);

            // Find the specific report we need
            const report = response.data.find(r => r.id === reportId);

            if (!report) {
                console.error('[PowerBI Init] ✗ Report not found in response');
                console.error('[PowerBI Init] Available report IDs:', response.data.map(r => r.id));
                throw new Error(`Report not found: ${reportId}`);
            }

            console.log('[PowerBI Init] ✓ Report found:', {
                id: report.id,
                name: report.name,
                embedUrl: report.embedUrl,
                hasToken: !!report.embedToken,
                tokenExpiration: report.tokenExpiration
            });

            // Calculate time until expiration
            if (report.tokenExpiration) {
                const now = new Date();
                const expiryDate = new Date(report.tokenExpiration);
                const minutesUntilExpiry = Math.round((expiryDate - now) / 1000 / 60);
                console.log(`[PowerBI Init] Token valid for: ${minutesUntilExpiry} minutes (expires at ${expiryDate.toLocaleTimeString()})`);
            }

            console.log('[PowerBI Init] Step 3: Updating React state...');

            // Update state with report data
            setAccessToken(report.embedToken);
            setEmbedUrl(report.embedUrl);
            setIsEmbedToken(report.isEmbedToken);
            setTokenExpiration(report.tokenExpiration); // Store token expiration time
            setDatasetId(report.datasetId); // Store dataset ID for DAX queries
            setCurrentReportName(report.name); // Store report name for drawer title

            const totalDuration = Date.now() - fetchStartTime;
            console.log('[PowerBI Init] ────────────────────────────────────────────');
            console.log(`[PowerBI Init] ✓ Initial data fetch SUCCESS (${totalDuration}ms)`);
            console.log('[PowerBI Init] Report will now embed');
            console.log('[PowerBI Init] ────────────────────────────────────────────');

            return report;

        } catch (error) {
            const totalDuration = Date.now() - fetchStartTime;

            console.error('┌─────────────────────────────────────────────────────────────┐');
            console.error('│ [PowerBI Init] ✗ Initial Data Fetch FAILED                 │');
            console.error('└─────────────────────────────────────────────────────────────┘');
            console.error(`[PowerBI Init] Duration: ${totalDuration}ms`);
            console.error('[PowerBI Init] Error:', error);
            console.error('[PowerBI Init] Error message:', error.message);
            console.error('[PowerBI Init] Stack:', error.stack);

            // Check if this is a session expiry (handled by execute.jsx)
            if (error.status === 401 || error.status === 403) {
                console.warn('[PowerBI Init] Session expired - user will be redirected to login');
                // Session expiry modal already shown by execute.jsx
                return;
            }

            // Show error modal for other errors
            // PATCH: Wrap Modal in try-catch to prevent unhandled rejections
            try {
                Modal.error({
                    title: 'Failed to Load Report',
                    content: error.message || 'Unable to load the report. Please try again or contact support.',
                    okText: 'OK'
                });
            } catch (modalError) {
                console.error('[PowerBI Init] Modal display failed:', modalError);
                // Fallback: Use native alert if Modal fails
                alert('Failed to Load Report\n\n' + (error.message || 'Unable to load the report. Please try again or contact support.'));
            }

            throw error;
        } finally {
            setIsInitializing(false);
        }
    };

    /**
     * Schedule proactive token refresh based on token expiration time
     * Microsoft Recommended Pattern for Embed Tokens (non-SaaS scenarios)
     * - Checks every 30 seconds
     * - Refreshes 10 minutes before expiration
     * - Uses setAccessToken() to update token without page reload
     */
    const scheduleTokenRefresh = () => {
        // Clear any existing timer
        if (refreshTimerRef.current) {
            clearInterval(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }

        if (!tokenExpiration || !reportRef.current) {
            console.log('[PowerBI Scheduler] ⏸️  Cannot schedule - missing data:', {
                hasTokenExpiration: !!tokenExpiration,
                hasReportRef: !!reportRef.current
            });
            return;
        }

        console.log('┌─────────────────────────────────────────────────────────────┐');
        console.log('│ [PowerBI Scheduler] ▶️  Token Refresh Scheduler STARTING    │');
        console.log('└─────────────────────────────────────────────────────────────┘');
        console.log('[PowerBI Scheduler] Token expires at:', tokenExpiration);

        const expiryDate = new Date(tokenExpiration);
        const now = new Date();
        const minutesUntilExpiry = Math.round((expiryDate - now) / 1000 / 60);
        console.log(`[PowerBI Scheduler] Time until expiration: ${minutesUntilExpiry} minutes`);
        console.log('[PowerBI Scheduler] Will refresh 10 minutes before expiry');
        console.log('[PowerBI Scheduler] Checking every 30 seconds...');

        // Microsoft recommendation: Check every 30 seconds
        refreshTimerRef.current = setInterval(async () => {
            const now = new Date();
            const expiryDate = new Date(tokenExpiration);
            const timeUntilExpiry = expiryDate.getTime() - now.getTime();
            const minutesUntilExpiry = Math.round(timeUntilExpiry / 1000 / 60);

            // Log heartbeat every 5 minutes
            if (minutesUntilExpiry % 5 === 0) {
                console.log(`[PowerBI Scheduler] ⏱️  Heartbeat: ${minutesUntilExpiry} minutes until refresh (expires at ${expiryDate.toLocaleTimeString()})`);
            }

            // Microsoft recommendation: Refresh 10 minutes before expiration
            if (timeUntilExpiry <= REFRESH_BEFORE_EXPIRY_MS && timeUntilExpiry > 0) {
                console.log('┌─────────────────────────────────────────────────────────────┐');
                console.log('│ [PowerBI Scheduler] 🔄 PROACTIVE REFRESH TRIGGERED          │');
                console.log('└─────────────────────────────────────────────────────────────┘');
                console.log(`[PowerBI Scheduler] Reason: ${minutesUntilExpiry} minutes until expiry (threshold: 10 min)`);
                console.log(`[PowerBI Scheduler] Current time: ${now.toLocaleTimeString()}`);
                console.log(`[PowerBI Scheduler] Token expires: ${expiryDate.toLocaleTimeString()}`);

                // Clear the interval to prevent multiple refresh attempts
                if (refreshTimerRef.current) {
                    clearInterval(refreshTimerRef.current);
                    refreshTimerRef.current = null;
                }

                // PostHog: Proactive refresh triggered
                try {
                    posthog.capture('PBI-proactive-token-refresh-triggered', {
                        reportId,
                        minutesUntilExpiry,
                        scheduledRefreshTime: REFRESH_BEFORE_EXPIRY_MS / 1000 / 60
                    });
                } catch (posthogError) {
                    console.error('[PostHog] Capture failed (non-critical):', posthogError);
                }

                try {
                    await refreshPowerBIToken(true); // true = proactive refresh
                    console.log('[PowerBI Scheduler] ✓ Proactive token refresh completed successfully');

                    // After successful refresh, tokenExpiration state will be updated
                    // and useEffect will call scheduleTokenRefresh again
                } catch (error) {
                    console.error('[PowerBI Scheduler] ✗ Proactive token refresh failed');
                    console.error('[PowerBI Scheduler] Error:', error);
                    // Error already handled by refreshPowerBIToken
                }
            } else if (timeUntilExpiry <= 0) {
                console.warn('┌─────────────────────────────────────────────────────────────┐');
                console.warn('│ [PowerBI Scheduler] ⚠️  TOKEN ALREADY EXPIRED               │');
                console.warn('└─────────────────────────────────────────────────────────────┘');
                console.warn(`[PowerBI Scheduler] Token expired ${Math.abs(minutesUntilExpiry)} minutes ago`);
                console.warn(`[PowerBI Scheduler] Expired at: ${expiryDate.toLocaleTimeString()}`);

                // Clear the interval
                if (refreshTimerRef.current) {
                    clearInterval(refreshTimerRef.current);
                    refreshTimerRef.current = null;
                }
            }
        }, 30000); // Check every 30 seconds (Microsoft recommendation)

        console.log('[PowerBI Scheduler] ✓ Scheduler started successfully');
    };

    /**
     * Fetch fresh Power BI token from backend
     * Called when Power BI token expires or proactively before expiration
     * @param {boolean} isProactive - True if this is a proactive refresh, false if reactive (error-triggered)
     */
    const refreshPowerBIToken = async (isProactive = false) => {
        // CRITICAL FIX: Prevent concurrent refresh calls using synchronous ref check
        // React state updates are async/batched, causing race conditions
        if (isRefreshingRef.current) {
            console.log('[PowerBI] Token refresh already in progress (detected via ref), skipping');
            return;
        }

        // CRITICAL FIX: Prevent infinite retry loops
        if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
            console.error('[PowerBI] Max retry attempts reached, stopping refresh');

            // PATCH: Wrap Modal in try-catch to prevent unhandled rejections
            try {
                Modal.error({
                    title: 'Report Refresh Failed',
                    content: 'Unable to refresh the report after multiple attempts. Please navigate away and back to the report.',
                });
            } catch (modalError) {
                console.error('[PowerBI] Modal display failed:', modalError);
                // Fallback: Use native alert if Modal fails
                alert('Report Refresh Failed\n\nUnable to refresh the report after multiple attempts. Please navigate away and back to the report.');
            }
            return;
        }

        try {
            // Set ref FIRST (synchronous) to prevent race conditions
            isRefreshingRef.current = true;
            // Then set state for UI updates
            setIsRefreshing(true);
            retryCountRef.current += 1;
            console.log('[PowerBI] Refreshing token for report:', reportId, `(attempt ${retryCountRef.current}/${MAX_RETRY_ATTEMPTS})`);

            // PostHog: Token refresh started
            try {
                posthog.capture('PBI-token-refresh-started', {
                    reportId,
                    attempt: retryCountRef.current,
                    maxAttempts: MAX_RETRY_ATTEMPTS,
                    isProactive // Track if this was proactive or reactive refresh
                });
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }

            const response = await execute(
                'POST',
                `${protectedResources.api.baseEndPoint}/pbi/refresh-token`,
                { reportId }
            );

            // CRITICAL FIX: Check response structure properly
            // Backend can return {success: false, message: "..."} or {success: true, data: {...}}
            if (!response) {
                throw new Error('Failed to refresh token: No response from server');
            }

            if (response.success === false) {
                // Backend explicitly returned failure
                const errorMsg = response.message || 'Failed to generate embed token';
                console.error('[PowerBI] Backend returned failure:', errorMsg);
                throw new Error(errorMsg);
            }

            if (!response.success || !response.data) {
                // Unexpected response format
                console.error('[PowerBI] Unexpected response format:', response);
                throw new Error('Failed to refresh token: Invalid response format');
            }

            // Success case
            console.log('[PowerBI] Token refreshed successfully');

            // PostHog: Token refresh succeeded
            try {
                posthog.capture('PBI-token-refresh-success', {
                    reportId,
                    attempt: retryCountRef.current,
                    duration: 'auto-calculated-by-posthog'
                });
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }

            // Reset retry counter on success
            retryCountRef.current = 0;

            // Update state with new token
            setAccessToken(response.data.token);
            setEmbedUrl(response.data.embedUrl);
            setIsEmbedToken(response.data.isEmbedToken);
            setTokenExpiration(response.data.tokenExpiration); // Store token expiration time
            setDatasetId(response.data.datasetId); // Update dataset ID

            // Apply the new token to the embedded report (for reactive/fallback refresh only)
            if (reportRef.current) {
                try {
                    console.log('[PowerBI] Applying refreshed token to embedded report...');
                    await reportRef.current.setAccessToken(response.data.token);
                    console.log('[PowerBI] ✓ Token refresh applied successfully');

                    try {
                        posthog.capture('PBI-fallback-token-applied', {
                            reportId,
                            expiresAt: response.data.tokenExpiration
                        });
                    } catch (posthogError) {
                        console.error('[PostHog] Capture failed (non-critical):', posthogError);
                    }
                } catch (error) {
                    console.error('[PowerBI] ✗ Failed to apply token refresh:', error);
                }
            }

            return response.data;
        } catch (error) {
            console.error('[PowerBI] Token refresh failed:', error);

            // PostHog: Token refresh failed
            try {
                posthog.capture('PBI-token-refresh-failed', {
                    reportId,
                    attempt: retryCountRef.current,
                    errorStatus: error.status || 'unknown',
                    errorMessage: error.message || 'Unknown error',
                    isSessionExpiry: error.status === 401 || error.status === 403
                });
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }

            // If backend returns 401/403, it's a session expiry
            if (error.status === 401 || error.status === 403) {
                console.log('[PowerBI] Backend session expired during token refresh');
                // showSessionExpiredModal is already called by execute.jsx
            } else if (error.status === 503 || error.errorType === 'CAPACITY_UNAVAILABLE') {
                // PowerBI capacity is paused or unavailable
                console.error('[PowerBI] PowerBI capacity is unavailable (paused or service issue)');

                // PATCH: Wrap Modal in try-catch to prevent unhandled rejections
                try {
                    Modal.warning({
                        title: 'Reporting Service Unavailable',
                        content: (
                            <div>
                                <p>The Reporting service is unavailable or is currently in maintenance.</p>
                                <p style={{ marginTop: '10px' }}>
                                    Please contact supportportal@thebicollab.com to report this issue.
                                </p>
                            </div>
                        ),
                        okText: 'OK'
                    });
                } catch (modalError) {
                    console.error('[PowerBI] Modal display failed:', modalError);
                    // Fallback: Use native alert if Modal fails
                    alert('Reporting Service Unavailable\n\nThe Reporting service is unavailable or is currently in maintenance.\n\nPlease contact supportportal@thebicollab.com to report this issue.');
                }
            } else {
                // Other errors - show user-friendly message with specific error
                const errorMessage = error.message || 'Unknown error occurred';
                const isMaxRetriesReached = retryCountRef.current >= MAX_RETRY_ATTEMPTS;

                // PATCH: Wrap Modal in try-catch to prevent unhandled rejections
                try {
                    Modal.error({
                        title: 'Report Refresh Failed',
                        content: isMaxRetriesReached
                            ? 'Unable to refresh the report after multiple attempts. Please try navigating away and back to the report, or contact support if the issue persists.'
                            : `Unable to refresh the report: ${errorMessage}. Please try again or navigate away and back to the report.`,
                        okText: 'OK'
                    });
                } catch (modalError) {
                    console.error('[PowerBI] Modal display failed:', modalError);
                    // Fallback: Use native alert if Modal fails
                    if (window.confirm('Report Refresh Failed\n\n' + (isMaxRetriesReached
                        ? 'Unable to refresh the report after multiple attempts. Please try navigating away and back to the report, or contact support if the issue persists.'
                        : `Unable to refresh the report: ${errorMessage}. Please try again or navigate away and back to the report.`) + '\n\nClick OK to acknowledge.')) {
                        // User acknowledged
                    }
                }
            }

            throw error;
        } finally {
            // Reset both ref and state to allow future refreshes
            isRefreshingRef.current = false;
            setIsRefreshing(false);
        }
    };

    /**
     * Check if Power BI error indicates token expiration
     * OPTIMIZED: More specific patterns to avoid false positives with capacity pause errors
     */
    const isPowerBITokenError = (event) => {
        const detail = event?.detail;
        const message = (detail?.message || '') + ' ' + (detail?.detailedMessage || '');
        const errorCode = detail?.errorCode || '';
        const messageLower = message.toLowerCase();
        const errorCodeLower = errorCode.toLowerCase();

        // SPECIFIC token error patterns (avoid generic patterns that match capacity errors)
        const tokenErrorPatterns = [
            'tokenexpired',
            'token expired',
            'token has expired',
            'access token',
            'bearer token',
            'authenticate',
            'authentication failed',
            'authentication required'
        ];

        // Check message patterns
        const messageMatch = tokenErrorPatterns.some(pattern =>
            messageLower.includes(pattern)
        );

        // Check error codes (be specific)
        const codeMatch = errorCodeLower === '401' ||
                         errorCodeLower === '403' ||
                         errorCodeLower === 'unauthorized';

        return messageMatch || codeMatch;
    };

    /**
     * Check if Power BI error indicates capacity is paused/inactive
     * Based on actual error captured from console when capacity is paused
     */
    const isPowerBICapacityPauseError = (event) => {
        const detail = event?.detail;
        const message = detail?.message || '';
        const detailedMessage = detail?.detailedMessage || '';
        const technicalDetails = detail?.technicalDetails || {};

        // Check for capacity pause error patterns
        // Pattern 1: Error message contains specific capacity pause keywords
        const messagePatterns = [
            'ExplorationContainer_FailedToLoadModel',
            'FailedToLoadModel',
            'Cannot load model',
            'Couldn\'t load the model schema'
        ];

        const messageMatch = messagePatterns.some(pattern =>
            message.includes(pattern) || detailedMessage.includes(pattern)
        );

        // Pattern 2: Check technicalDetails.errorInfo for CapacityNotActive
        let capacityErrorFound = false;
        if (technicalDetails.errorInfo && Array.isArray(technicalDetails.errorInfo)) {
            capacityErrorFound = technicalDetails.errorInfo.some(info => {
                const value = info.value || '';
                return value.includes('CapacityNotActive') || value.includes('Capacity operation failed');
            });
        }

        // Return true if either pattern matches
        return messageMatch || capacityErrorFound;
    };

    const logActivity = (event, report) => {
        console.log("PowerBIEmbed event ["+event.type+"]: ", event, report);
        if(event.type === 'rendered'){
            report.getFilters().then(filters => {
                var payload = { filters: filters.map(filter => ({ operator: filter.operator, ...filter.target, values: filter.values, ...filter.displaySettings })) };
                console.log("PowerBIEmbed " + event.type, payload);
                // PATCH: Wrap PostHog capture in try-catch to prevent unhandled rejections
                try {
                    posthog.capture('PBI-report-event-rendered', payload);
                } catch (posthogError) {
                    console.error('[PostHog] Capture failed (non-critical):', posthogError);
                }
            }).catch(filterError => {
                // PATCH: Catch promise rejection from getFilters()
                console.error('[PowerBI] Failed to get filters for rendered event:', filterError);
            });

            // Get initial page name when report is first rendered
            report.getPages().then(async (pages) => {
                const activePage = pages.find(p => p.isActive);
                if (activePage) {
                    setCurrentPageName(activePage.displayName);
                    console.log('[PowerBI] Initial page set:', activePage.displayName);

                    // Check for cached AI summary (only once per report load)
                    if (!cacheCheckRef.current && reportId && userDetails?.CustomerID) {
                        cacheCheckRef.current = true;

                        // Store report reference for regenerate functionality
                        reportRef.current = report;

                        console.log('[AI Summary Cache] 🔍 Checking cache on render event...');
                        console.log('[AI Summary Cache] Report:', reportId, '| Page:', activePage.name);

                        try {
                            // Use execute hook which handles MSAL authentication automatically
                            const params = new URLSearchParams();
                            params.append('pageId', activePage.name);
                            params.append('organizationId', userDetails.CustomerID);

                            const url = `${protectedResources.api.baseEndPoint}/v1/summarize/report/${reportId}?${params.toString()}`;
                            const response = await execute('GET', url);

                            console.log('[AI Summary Cache] Cache response:', response);

                            if (response?.success && response?.data) {
                                console.log('[AI Summary Cache] ✓ CACHE HIT - Summary available');
                                setHasCachedSummary(true);
                            } else {
                                console.log('[AI Summary Cache] ✗ CACHE MISS - No cached summary');
                                setHasCachedSummary(false);
                            }
                        } catch (cacheError) {
                            console.error('[AI Summary Cache] Cache check failed:', cacheError);
                            // Silent failure - cache is optional
                        }
                    } else {
                        console.log('[AI Summary Cache] Skipping cache check:', {
                            alreadyChecked: cacheCheckRef.current,
                            hasReportId: !!reportId,
                            hasCustomerId: !!userDetails?.CustomerID
                        });
                    }
                }
            }).catch(pageError => {
                console.error('[PowerBI] Failed to get pages for rendered event:', pageError);
            });
        }

        if(event.type === 'pageChanged'){
            var payload = {};
            const newPage = event?.detail?.newPage;
            if (newPage) {
                payload = { ...payload, page: newPage.displayName };
                // Update current page name for drawer title
                setCurrentPageName(newPage.displayName);
                // Close summary panel when page changes to avoid showing stale data
                setShowSummaryPanel(false);
                // Reset auto-open flag so it doesn't auto-open with old data
                setAutoOpenDrawer(false);
                // Allow cache check for new page
                cacheCheckRef.current = false;

                // Check cache for the new page
                if (reportId && userDetails?.CustomerID) {
                    cacheCheckRef.current = true;

                    // Store report reference for regenerate functionality
                    if (!reportRef.current) {
                        reportRef.current = report;
                    }

                    console.log('[AI Summary Cache] 🔍 Page changed - checking cache for new page...');
                    console.log('[AI Summary Cache] Report:', reportId, '| Page:', newPage.name);

                    (async () => {
                        try {
                            const params = new URLSearchParams();
                            params.append('pageId', newPage.name);
                            params.append('organizationId', userDetails.CustomerID);

                            const url = `${protectedResources.api.baseEndPoint}/v1/summarize/report/${reportId}?${params.toString()}`;
                            const response = await execute('GET', url);

                            console.log('[AI Summary Cache] Cache response:', response);

                            if (response?.success && response?.data) {
                                console.log('[AI Summary Cache] ✓ CACHE HIT - Summary available for new page');
                                setHasCachedSummary(true);
                            } else {
                                console.log('[AI Summary Cache] ✗ CACHE MISS - No cached summary for new page');
                                setHasCachedSummary(false);
                            }
                        } catch (cacheError) {
                            console.error('[AI Summary Cache] Cache check failed:', cacheError);
                        }
                    })();
                }
            }

            console.log('PowerBIEmbed ' + event.type, payload);
            // PATCH: Wrap PostHog capture in try-catch to prevent unhandled rejections
            try {
                posthog.capture('PBI-report-event' + event.type, payload);
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }

        }
        else if(event.type === 'dataSelected' || event.type === 'visualClicked' || event.type === 'selectionChanged') {
            var payload = {};
            const page = event?.detail?.page?.displayName;
            if (page) {
                payload = { ...payload, page: page };
            }
            const report = event?.detail?.report?.displayName;
            if (report) {
                payload = { ...payload, report: report };
            }
            const visual = event?.detail?.visual?.name;
            if (visual) {
                payload = { ...payload, visual: visual, visual_type: event?.detail?.visual?.type, visual_title: event?.detail?.visual?.title };
            }
            const dataPoints = event?.detail?.dataPoints?.map((dataPoint) => {
                return {
                    value: dataPoint?.identity[0].equals,
                    ...dataPoint?.identity[0].target
                };
            });
            if (dataPoints?.length) {
                payload = { ...payload, dataPoints: dataPoints };
            }
            console.log('PowerBIEmbed ' + event.type, payload);
            // PATCH: Wrap PostHog capture in try-catch to prevent unhandled rejections
            try {
                posthog.capture('PBI-report-event-' + event.type, payload);
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }
        }
    };

    // CRITICAL: Handle navigation and initial data fetch
    // This useEffect runs when reportId changes and fetches fresh tokens
    useEffect(() => {
        if (!reportId) return;

        console.log('═══════════════════════════════════════════════════════════════');
        console.log('[PowerBI Navigation] Report ID changed:', reportId);
        console.log('[PowerBI Navigation] Current state:', {
            accessToken: accessToken ? `${accessToken.substring(0, 20)}...` : 'null',
            embedUrl: embedUrl || 'null',
            tokenExpiration: tokenExpiration || 'null',
            isEmbedToken,
            isInitializing
        });

        // CRITICAL FIX: Always fetch fresh tokens on navigation
        // Don't rely on cached tokens from location.state as they may be stale
        // The Sidebar no longer passes tokens, so we always fetch fresh ones

        console.log('[PowerBI Navigation] ✓ Clearing cached state and fetching fresh token');
        console.log('[PowerBI Navigation] Reason: Sidebar no longer passes tokens to prevent stale token usage');

        // Reset state when switching to a new report
        setAccessToken(null);
        setEmbedUrl(null);
        setIsEmbedToken(undefined);
        setTokenExpiration(null);
        setDatasetId(null);

        // Reset retry counter and refresh flags for new report
        retryCountRef.current = 0;
        isRefreshingRef.current = false;
        setIsRefreshing(false);

        // Reset cache check flag for new report
        cacheCheckRef.current = false;
        setHasCachedSummary(false);
        setAutoOpenDrawer(false);

        console.log('[PowerBI Navigation] State reset complete - fetching fresh data...');
        console.log('═══════════════════════════════════════════════════════════════');

        // Immediately fetch fresh token data
        fetchInitialReportData();
    }, [reportId]);

    useEffect(() => {
        // Development: Log report state for debugging (fires during initialization before fetch completes)
        if (process.env.NODE_ENV === 'development') {
            if (!reportId || !embedUrl || !accessToken || isEmbedToken === undefined) {
                console.log('[PowerBI Debug] Initialization in progress - Report state:', {
                    reportId: reportId || 'none',
                    hasEmbedUrl: !!embedUrl,
                    hasAccessToken: !!accessToken,
                    isEmbedToken,
                    tokenExpiration: tokenExpiration || 'none'
                });
            } else {
                console.log('[PowerBI Debug] Report fully initialized:', {
                    reportId,
                    hasEmbedUrl: !!embedUrl,
                    hasAccessToken: !!accessToken,
                    isEmbedToken,
                    tokenExpiration
                });
            }
        }

        // Development: Expose token refresh function for manual testing
        if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
            // Test 1: Full token refresh (already exists)
            window.testPowerBITokenRefresh = async () => {
                console.log('[TEST] Manually triggering PowerBI token refresh...');
                try {
                    await refreshPowerBIToken();
                    console.log('[TEST] Token refresh completed successfully');
                } catch (error) {
                    console.error('[TEST] Token refresh failed:', error);
                }
            };

            // Test 2: Simulate token expiry error
            window.testPowerBITokenExpiry = async () => {
                console.log('[TEST] Simulating PowerBI token expiry error...');

                // Safety guards
                if (!reportId) {
                    console.error('[TEST] Cannot test: No reportId available. Please load a report first.');
                    return;
                }

                if (isRefreshing) {
                    console.warn('[TEST] Cannot test: Token refresh already in progress.');
                    return;
                }

                const mockEvent = {
                    type: 'error',
                    detail: {
                        message: 'TokenExpired: The access token has expired',
                        errorCode: 'TokenExpired',
                        detailedMessage: 'Get report failed - Token has expired'
                    }
                };
                console.log('[TEST] Triggering error detection with mock event:', mockEvent);
                if (isPowerBITokenError(mockEvent)) {
                    console.log('[TEST] Token error detected, triggering refresh...');
                    try {
                        await refreshPowerBIToken();
                        console.log('[TEST] ✓ Token refresh completed successfully');
                    } catch (error) {
                        console.error('[TEST] ✗ Token refresh failed:', error.message);
                    }
                } else {
                    console.warn('[TEST] Token error NOT detected - check isPowerBITokenError logic');
                }
            };

            // Test 3: Trigger various PowerBI errors
            window.triggerPowerBIError = async (errorType = 'token') => {
                console.log(`[TEST] Triggering PowerBI error type: ${errorType}`);

                // Safety guards
                if (!reportId) {
                    console.error('[TEST] Cannot test: No reportId available. Please load a report first.');
                    return;
                }

                if (isRefreshing) {
                    console.warn('[TEST] Cannot test: Token refresh already in progress.');
                    return;
                }

                const mockEvents = {
                    token: {
                        type: 'error',
                        detail: { message: 'Get report failed', errorCode: '401' }
                    },
                    network: {
                        type: 'error',
                        detail: { message: 'Network error occurred', errorCode: 'NetworkError' }
                    },
                    permission: {
                        type: 'error',
                        detail: { message: 'Unauthorized access', errorCode: '403' }
                    }
                };
                const mockEvent = mockEvents[errorType] || mockEvents.token;
                console.log('[TEST] Mock event:', mockEvent);
                if (isPowerBITokenError(mockEvent)) {
                    console.log('[TEST] Token error detected, triggering refresh...');
                    try {
                        await refreshPowerBIToken();
                        console.log('[TEST] ✓ Token refresh completed successfully');
                    } catch (error) {
                        console.error('[TEST] ✗ Token refresh failed:', error.message);
                    }
                } else {
                    console.log('[TEST] Not a token error, would show generic error modal');
                }
            };

            // Test 4: Clear PowerBI token state
            window.clearPowerBIToken = () => {
                console.log('[TEST] Clearing PowerBI token state...');
                console.log('[TEST] Before clear:', { accessToken, embedUrl, isEmbedToken });
                setAccessToken('');
                setEmbedUrl('');
                console.log('[TEST] Token state cleared. Report will fail to load until refreshed.');
                console.log('[TEST] Use window.testPowerBITokenRefresh() to recover.');
            };

            // Test 5: Get current token info
            window.getPowerBITokenInfo = () => {
                const now = new Date();
                const expiryDate = tokenExpiration ? new Date(tokenExpiration) : null;
                const timeUntilExpiry = expiryDate ? expiryDate.getTime() - now.getTime() : null;

                const info = {
                    reportId,
                    hasAccessToken: !!accessToken,
                    tokenLength: accessToken?.length || 0,
                    embedUrl,
                    isEmbedToken,
                    tokenExpiration,
                    expiresAt: expiryDate?.toISOString() || 'unknown',
                    minutesUntilExpiry: timeUntilExpiry ? Math.round(timeUntilExpiry / 1000 / 60) : null,
                    hasScheduledRefresh: !!refreshTimerRef.current,
                    isRefreshing,
                    retryCount: retryCountRef.current,
                    maxRetries: MAX_RETRY_ATTEMPTS
                };
                console.log('[TEST] PowerBI Token Info:', info);
                return info;
            };

            // Test 6: Test proactive token refresh
            window.testProactiveRefresh = async () => {
                console.log('[TEST] Testing proactive token refresh...');
                try {
                    await refreshPowerBIToken(true);
                    console.log('[TEST] ✓ Proactive refresh completed');
                } catch (error) {
                    console.error('[TEST] ✗ Proactive refresh failed:', error);
                }
            };

        }

        // Cleanup on unmount
        return () => {
            console.log('[PowerBI] Component unmounting, cleaning up...');
            // Reset refresh flags to prevent state updates on unmounted component
            isRefreshingRef.current = false;
            setIsRefreshing(false);
            // Clear any scheduled token refresh timers
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [reportId, embedUrl, accessToken, isEmbedToken]);

    // MICROSOFT RECOMMENDED: Schedule proactive token refresh when token expiration is known
    // - Monitors token expiration every 30 seconds
    // - Refreshes 10 minutes before expiration
    // - Uses setAccessToken() for seamless refresh without page reload
    useEffect(() => {
        if (tokenExpiration && reportRef.current && !isInitializing) {
            console.log('[PowerBI] Token expiration updated, scheduling proactive refresh');
            scheduleTokenRefresh();
        }

        // Cleanup timer when component unmounts or token expiration changes
        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [tokenExpiration, reportRef.current, isInitializing]);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.type === 'toggle-width') {
                setDrawerExpanded(!!event.data.expanded);
            } else if (event.data?.type === 'regenerate') {
                handleRegenerateSummary();
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    /**
     * Check for cached AI summary - moved to 'rendered' event handler
     * where getPages() already works reliably
     */

    /**
     * Dev Mode: Dump Power BI report data for offline analysis
     */
    const handleDumpReportData = async () => {
        if (!reportRef.current) {
            message.error('Report not loaded yet');
            return;
        }

        setIsDumpingData(true);
        const hideLoading = message.loading('Extracting Power BI data...', 0);

        try {
            const report = reportRef.current;

            // Use dataset ID from state (loaded from backend)
            console.log('[DataDump] Using dataset ID from state:', datasetId);

            // Get active page
            const pages = await report.getPages();
            const activePage = pages.find(p => p.isActive) || pages[0];

            // Get all visuals on the page
            const allVisuals = await activePage.getVisuals();

            // Export ALL visuals - no filtering
            const visuals = allVisuals;

            console.log(`[DataDump] Export mode: ALL VISUALS (no filtering)`);
            console.log(`[DataDump] Found ${visuals.length} visuals to export: ${visuals.map(v => `${v.title}(${v.type})`).join(', ')}`);

            // Extract data from each visual
            const extractedData = {
                visuals: []
            };

            // Use shared extractor function (no code duplication)
            for (let i = 0; i < visuals.length; i++) {
                const visual = visuals[i];
                console.log(`[DataDump] Exporting visual ${i + 1}/${visuals.length}: ${visual.title} (${visual.type})...`);

                // Use shared extractVisualData function
                const visualData = await extractVisualData(visual, 500);

                // Convert to dump format
                extractedData.visuals.push({
                    name: visualData.visualId,
                    title: visualData.title,
                    type: visualData.visualType,
                    rows: visualData.rows || [],
                    rowCount: visualData.rows?.length || 0,
                    exportType: visualData.exportType,
                    error: visualData.error,
                    errorDetails: visualData.errorDetails
                });
            }

            // Get Power BI embed credentials for Puppeteer screenshot
            console.log('[DataDump] Preparing Power BI embed credentials for screenshot...');

            // Capture current report state (filters, slicers, etc.)
            console.log('[DataDump] Capturing report state (filters, slicers, selections)...');
            const reportState = await captureReportState(report, activePage);

            if (reportState) {
                console.log('[DataDump] ✓ Report state captured successfully');
                console.log('[DataDump] State summary:', {
                    reportFilters: reportState.filters.report.length,
                    pageFilters: reportState.filters.page.length,
                    slicers: reportState.slicers.length
                });
            } else {
                console.warn('[DataDump] ⚠ Failed to capture report state, screenshot will use default view');
            }

            // Simple dump: backend captures screenshot AND saves files (one call!)
            console.log('[DataDump] Sending data to backend (will capture screenshot + save files)...');

            // Use simple-dump endpoint (all-in-one: screenshot + JSON + PNG)
            const response = await fetch('http://localhost:3001/public/simple-dump', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    reportId: reportId,
                    reportName: document.title || 'Power BI Report',
                    datasetId: datasetId,
                    pageId: activePage.name,
                    pageName: activePage.displayName,
                    extractedData: extractedData,
                    embedUrl: embedUrl,  // Power BI embed URL
                    accessToken: accessToken,  // Power BI access token
                    reportState: reportState,  // ← NEW: User's filter/slicer state
                    metadata: {
                        url: window.location.href,
                        timestamp: new Date().toISOString(),
                        userAgent: navigator.userAgent,
                        datasetId: datasetId,
                        exportMode: 'ALL_VISUALS',
                        exportDescription: reportState ? 'All visuals with current filter state' : 'All visuals without filtering',
                        hasFilterState: !!reportState
                    }
                })
            });

            const result = await response.json();

            hideLoading();

            if (result.success) {
                // Show message notification
                const screenshotInfo = result.files.png ? ` (with screenshot)` : '';
                message.success(`Dumped ${result.stats.visualCount} visuals, ${result.stats.totalRows} rows${screenshotInfo}!`, 5);

                // Show detailed modal
                Modal.success({
                    title: 'Power BI Data Dumped Successfully!',
                    content: (
                        <div>
                            <p><strong>JSON File:</strong> {result.files.json.filename}</p>
                            <p><strong>Visuals:</strong> {result.stats.visualCount}</p>
                            <p><strong>Rows:</strong> {result.stats.totalRows}</p>
                            <p><strong>JSON Size:</strong> {(result.files.json.size / 1024).toFixed(2)} KB</p>

                            {result.files.png && (
                                <>
                                    <p style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #eee' }}>
                                        <strong>Screenshot File:</strong> {result.files.png.filename}
                                    </p>
                                    <p>
                                        <strong>Screenshot Size:</strong> {(result.files.png.size / 1024).toFixed(2)} KB
                                    </p>
                                    <p style={{ fontSize: 12, color: '#888' }}>
                                        (Captured via Puppeteer from localhost)
                                    </p>
                                </>
                            )}

                            <p style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
                                Saved to: {result.files.json.filepath}
                            </p>
                        </div>
                    ),
                    width: 600
                });
            } else {
                message.error(`Failed to dump: ${result.error}`);
            }

        } catch (error) {
            hideLoading();
            message.error(`Error: ${error.message}`);
        } finally {
            setIsDumpingData(false);
        }
    };

    // Dev-only diagnostic buttons (blue/green dump actions).
    // Disabled by default; enable explicitly with REACT_APP_ENABLE_DEV_DUMPS=true.
    const enableDevDumps =
        process.env.REACT_APP_ENABLE_DEV_DUMPS === 'true' &&
        (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost');

    /**
     * Capture current report state (filters, slicers, bookmarks)
     * This state will be applied in the headless browser for screenshot capture
     */
    const captureReportState = async (report, activePage) => {
        try {
            console.log('[Report State] Capturing current report state...');

            const state = {
                filters: {
                    report: [],
                    page: [],
                    visual: []
                },
                slicers: [],
                activePage: null,
                bookmarkState: null
            };

            // 1. Capture report-level filters
            try {
                const reportFilters = await report.getFilters();
                state.filters.report = reportFilters || [];
                console.log(`[Report State] ✓ Captured ${state.filters.report.length} report-level filters`);
            } catch (err) {
                console.warn('[Report State] Failed to get report filters:', err.message);
            }

            // 2. Capture page-level filters
            try {
                const pageFilters = await activePage.getFilters();
                state.filters.page = pageFilters || [];
                console.log(`[Report State] ✓ Captured ${state.filters.page.length} page-level filters`);
            } catch (err) {
                console.warn('[Report State] Failed to get page filters:', err.message);
            }

            // 3. Capture slicer states (selected values)
            try {
                const visuals = await activePage.getVisuals();
                const slicers = visuals.filter(v => v.type === 'slicer');

                console.log(`[Report State] Found ${slicers.length} slicers on page`);

                for (const slicer of slicers) {
                    try {
                        const slicerState = await slicer.getSlicerState();
                        state.slicers.push({
                            name: slicer.name,
                            title: slicer.title,
                            state: slicerState
                        });
                        console.log(`[Report State] ✓ Captured slicer: ${slicer.title || slicer.name}`);
                    } catch (err) {
                        console.warn(`[Report State] Failed to get slicer state for ${slicer.name}:`, err.message);
                    }
                }
            } catch (err) {
                console.warn('[Report State] Failed to get slicers:', err.message);
            }

            // 4. Capture active page info
            state.activePage = {
                name: activePage.name,
                displayName: activePage.displayName
            };

            // 5. Capture bookmarks (optional - for advanced scenarios)
            try {
                if (report.bookmarksManager) {
                    const bookmarkState = await report.bookmarksManager.capture({
                        personalizeVisuals: true,
                        allPages: false
                    });
                    state.bookmarkState = bookmarkState;
                    console.log('[Report State] ✓ Captured bookmark state');
                }
            } catch (err) {
                console.warn('[Report State] Failed to capture bookmark:', err.message);
            }

            console.log('[Report State] ✓ State capture complete:', {
                reportFilters: state.filters.report.length,
                pageFilters: state.filters.page.length,
                slicers: state.slicers.length,
                hasBookmark: !!state.bookmarkState
            });

            return state;

        } catch (error) {
            console.error('[Report State] Error capturing report state:', error);
            return null;
        }
    };

    /**
     * Extract table names from report using Power BI SDK
     * Analyzes all pages, visuals, filters, and slicers to find table references
     */
    const extractTableNamesFromReport = async () => {
        const report = reportRef.current;
        const tableNames = new Set();

        try {
            // Get all pages
            const pages = await report.getPages();

            for (const page of pages) {
                // Get page-level filters
                try {
                    const pageFilters = await page.getFilters();
                    pageFilters.forEach(filter => {
                        if (filter.target?.table) {
                            tableNames.add(filter.target.table);
                        }
                    });
                } catch (err) {
                    console.log('[TableExtract] Could not get page filters:', err.message);
                }

                // Get all visuals on the page
                const visuals = await page.getVisuals();

                for (const visual of visuals) {
                    try {
                        // Get visual-level filters
                        const visualFilters = await visual.getFilters();
                        visualFilters.forEach(filter => {
                            if (filter.target?.table) {
                                tableNames.add(filter.target.table);
                            }
                        });
                    } catch (err) {
                        console.log('[TableExtract] Could not get visual filters:', err.message);
                    }

                    // For slicers, try to get slicer state which includes field info
                    if (visual.type === 'slicer') {
                        try {
                            const slicerState = await visual.getSlicerState();
                            if (slicerState?.targets?.[0]?.table) {
                                tableNames.add(slicerState.targets[0].table);
                            }
                        } catch (err) {
                            console.log('[TableExtract] Could not get slicer state:', err.message);
                        }
                    }
                }
            }

            // Get report-level filters
            try {
                const reportFilters = await report.getFilters();
                reportFilters.forEach(filter => {
                    if (filter.target?.table) {
                        tableNames.add(filter.target.table);
                    }
                });
            } catch (err) {
                console.log('[TableExtract] Could not get report filters:', err.message);
            }

            console.log('[TableExtract] Extracted table names:', Array.from(tableNames));
            return Array.from(tableNames);

        } catch (error) {
            console.error('[TableExtract] Error extracting table names:', error);
            return [];
        }
    };

    /**
     * Dev Mode: Dump entire dataset using SDK-extracted table names
     * Extracts table names from report visuals, then queries each table
     */
    const handleDumpDataset = async () => {
        if (!reportRef.current) {
            message.error('Report not loaded yet');
            return;
        }

        // Use dataset ID from state (loaded from backend)
        if (!datasetId) {
            message.error('Dataset ID not available. Please wait for report to load completely.');
            return;
        }

        // Extract table names from report using SDK
        const hideExtract = message.loading('Analyzing report to find table names...', 0);
        const extractedTables = await extractTableNamesFromReport();
        hideExtract();

        console.log('[DatasetDump] Extracted table names from SDK:', extractedTables);

        if (extractedTables.length > 0) {
            message.success(`Found ${extractedTables.length} table(s) from report!`, 3);
        }

        // Show modal to get table names from user
        let tableNamesInput = extractedTables.join(', ');

        Modal.confirm({
            title: 'Dataset Dump - Table Names',
            width: 600,
            content: (
                <div>
                    {extractedTables.length > 0 && (
                        <div style={{ marginBottom: 15, padding: 10, backgroundColor: '#f0f9ff', borderRadius: 4 }}>
                            <p style={{ margin: 0, fontSize: 13, color: '#0066cc', fontWeight: 500 }}>
                                ✓ Found {extractedTables.length} table(s) from report filters and visuals
                            </p>
                        </div>
                    )}

                    <p><strong>Table Names to Dump</strong></p>
                    <p style={{ fontSize: 12, color: '#666' }}>
                        {extractedTables.length > 0
                            ? 'Review and edit the table names below, or add more (comma-separated):'
                            : 'Enter table names to dump (comma-separated):'}
                    </p>
                    <p style={{ fontSize: 12, color: '#888', marginTop: 5 }}>
                        Example: Individuals, Risk Factors, HRST Scores, Clinical Review
                    </p>
                    <textarea
                        defaultValue={tableNamesInput}
                        placeholder="Table1, Table2, Table3..."
                        style={{
                            width: '100%',
                            marginTop: 10,
                            padding: 8,
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            minHeight: 100,
                            fontFamily: 'monospace',
                            fontSize: 12
                        }}
                        onChange={(e) => { tableNamesInput = e.target.value; }}
                    />
                    {extractedTables.length === 0 && (
                        <p style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
                            Note: No tables detected from filters. You'll need to enter them manually.
                        </p>
                    )}
                </div>
            ),
            onOk: async () => {
                const tableNames = tableNamesInput
                    .split(',')
                    .map(t => t.trim())
                    .filter(t => t.length > 0);

                // Allow empty array for auto-discovery
                await performDatasetDump(tableNames.length > 0 ? tableNames : null);
            },
            okText: 'Start Dump',
            cancelText: 'Cancel'
        });
    };

    /**
     * Perform the actual dataset dump with specified table names (or auto-discover)
     */
    const performDatasetDump = async (tableNames) => {
        setIsDumpingDataset(true);
        const hideLoading = message.loading(
            tableNames ? `Extracting ${tableNames.length} tables from dataset...` : 'Auto-discovering and extracting all tables...',
            0
        );

        try {
            console.log('[DatasetDump] Using dataset ID from state:', datasetId);
            console.log('[DatasetDump] Table names:', tableNames);

            // Send to backend with optional table names
            const requestBody = {
                datasetId: datasetId,
                reportId: reportId,
                reportName: document.title || 'Power BI Report'
            };

            if (tableNames && tableNames.length > 0) {
                requestBody.tableNames = tableNames;
            }

            const response = await fetch('http://localhost:3001/public/dump/dataset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();

            hideLoading();

            if (result.success) {
                // Show success message
                message.success(`Dumped ${result.stats.tableCount} tables, ${result.stats.totalRows} total rows!`, 5);

                // Show detailed modal with table breakdown
                Modal.success({
                    title: 'Complete Dataset Dumped Successfully!',
                    content: (
                        <div>
                            <p><strong>Method:</strong> Auto-Discovery DAX</p>
                            <p><strong>File:</strong> {result.filename}</p>
                            <p><strong>Tables:</strong> {result.stats.tableCount}</p>
                            <p><strong>Total Rows:</strong> {result.stats.totalRows}</p>
                            <p><strong>Size:</strong> {(result.fileSize / 1024).toFixed(2)} KB</p>

                            <div style={{ marginTop: 10, maxHeight: 200, overflow: 'auto' }}>
                                <strong>Tables extracted:</strong>
                                <ul style={{ fontSize: 12, marginTop: 5 }}>
                                    {result.stats.tables.map((table, idx) => (
                                        <li key={idx}>
                                            {table.name}: {table.rows} rows, {table.columns} columns
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <p style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
                                Saved to: {result.filepath}
                            </p>
                        </div>
                    ),
                    width: 600
                });
            } else {
                message.error(`Failed to dump dataset: ${result.error}`);
            }

        } catch (error) {
            hideLoading();
            message.error(`Error: ${error.message}`);
        } finally {
            setIsDumpingDataset(false);
        }
    };

    /**
     * Handle regenerate summary - closes drawer and reopens with skipCache=true
     */
    const handleRegenerateSummary = () => {
        // Close the drawer and reopen with skipCache=true to force regeneration
        setShowSummaryPanel(false);
        setForceSkipCache(true);
        setAutoOpenDrawer(true);
        setTimeout(() => {
            setShowSummaryPanel(true);
        }, 100);
    };
    // Check module access before rendering
    const moduleAccess = hasModuleAccess();

    // If user doesn't have access to the module, show access denied
    if (moduleAccess === false) {
        return (
            <div className="flex flex-col w-[100%] gap-5">
                <TitleComponent title={"Access Denied"} />
                <p>You do not have access to this report module.</p>
            </div>
        );
    }

    // If still loading user details, show loading state
    if (moduleAccess === undefined) {
        return (
            <div className="flex flex-col w-[100%] gap-5">
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className='flex flex-col w-full gap-5 relative'>
            {isInitializing ? (
                <div className='w-full h-[80vh] flex justify-center items-center'>
                    <Spin size="large" tip="Loading report..." />
                </div>
            ) : (
                <>
                    <div className='w-full mt-[28px] flex justify-center items-end'>
                        <PowerBIEmbed
                    embedConfig={{
                        type: 'report',
                        id: reportId,
                        embedUrl: embedUrl,
                        accessToken: accessToken,
                        tokenType: isEmbedToken === true ? models.TokenType.Embed : models.TokenType.Aad,
                        settings: {
                            panes: {
                                filters: {
                                    expanded: false,
                                    visible: true
                                }
                            },
                            background: models.BackgroundType.Transparent,
                            layoutType: models.LayoutType.Custom,
                            customLayout: {
                                displayOption: models.DisplayOption.FitToWidth
                            },
                        }
                    }}
                    eventHandlers={new Map([
                        ['loaded', function (event, entity) {
                            console.log("PowerBIEmbed event [loaded]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['saved', function (event, entity) { 
                            console.log("PowerBIEmbed event [saved]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['rendered', logActivity],
                        ['saveAsTriggered', function (event, entity) { 
                            console.log("PowerBIEmbed event [saveAsTriggered]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['error', async function (event, entity) {
                            // PATCH: Wrap entire async handler in try-catch to prevent unhandled promise rejections in production
                            try {
                                console.log("PowerBIEmbed event [error]: ", event, entity);
                                logActivity(event);

                                // ENHANCED LOGGING: Capture full error details for capacity pause detection
                                console.log('========== PowerBI ERROR DETAILS ==========');
                                console.log('Event Type:', event?.type);
                                console.log('Error Message:', event?.detail?.message);
                                console.log('Error Code:', event?.detail?.errorCode);
                                console.log('Detailed Message:', event?.detail?.detailedMessage);
                                console.log('Level:', event?.detail?.level);
                                console.log('Technical Details:', event?.detail?.technicalDetails);
                                console.log('Request ID:', event?.detail?.requestId);
                                console.log('Timestamp:', event?.detail?.timestamp);
                                console.log('Full Detail Object:', JSON.stringify(event?.detail, null, 2));
                                console.log('==========================================');

                                // PRIORITY 1: Check if this is a capacity pause error (most specific)
                                if (isPowerBICapacityPauseError(event)) {
                                    console.error('[PowerBI] Capacity pause/inactive detected');

                                    // PATCH: Wrap PostHog capture in try-catch to prevent failures
                                    try {
                                        posthog.capture('PBI-capacity-paused', {
                                            reportId,
                                            errorMessage: event?.detail?.message,
                                            detailedMessage: event?.detail?.detailedMessage
                                        });
                                    } catch (posthogError) {
                                        console.error('[PowerBI] PostHog capture failed (non-critical):', posthogError);
                                    }

                                    // PATCH: Wrap Modal in try-catch with fallback
                                    try {
                                        Modal.warning({
                                            title: 'Reporting Service Unavailable',
                                            content: (
                                                <div>
                                                    <p>The Reporting service is unavailable or is currently in maintenance.</p>
                                                    <p style={{ marginTop: '10px' }}>
                                                        Please contact supportportal@thebicollab.com to report this issue.
                                                    </p>
                                                </div>
                                            ),
                                            okText: 'OK'
                                        });
                                    } catch (modalError) {
                                        console.error('[PowerBI] Modal display failed:', modalError);
                                        // Fallback: Use native alert if Modal fails
                                        alert('Reporting Service Unavailable\n\nThe Reporting service is unavailable or is currently in maintenance.\n\nPlease contact supportportal@thebicollab.com to report this issue.');
                                    }

                                    return; // Stop processing, don't attempt token refresh
                                }

                            // PRIORITY 2: Check if this is a token expiration error
                            if (isPowerBITokenError(event)) {
                                console.log('[PowerBI] Token expiration detected, attempting refresh...');

                                try {
                                    // Refresh the token
                                    const newTokenData = await refreshPowerBIToken();

                                    if (newTokenData && entity) {
                                        console.log('[PowerBI] Applying new token to embedded report...');

                                        // CRITICAL FIX: Explicitly call setAccessToken on the embed instance
                                        // Simply updating React state is NOT enough - the iframe needs to be told to use the new token
                                        try {
                                            await entity.setAccessToken(newTokenData.token);
                                            console.log('[PowerBI] ✓ New token applied successfully via setAccessToken()');

                                            // PATCH: Wrap PostHog capture in try-catch
                                            try {
                                                posthog.capture('PBI-token-applied-to-iframe', {
                                                    reportId,
                                                    tokenType: newTokenData.isEmbedToken ? 'Embed' : 'AAD'
                                                });
                                            } catch (posthogError) {
                                                console.error('[PowerBI] PostHog capture failed (non-critical):', posthogError);
                                            }
                                        } catch (setTokenError) {
                                            console.error('[PowerBI] ✗ Failed to apply token to iframe:', setTokenError);

                                            // PATCH: Wrap PostHog capture in try-catch
                                            try {
                                                posthog.capture('PBI-token-application-failed', {
                                                    reportId,
                                                    error: setTokenError.message
                                                });
                                            } catch (posthogError) {
                                                console.error('[PowerBI] PostHog capture failed (non-critical):', posthogError);
                                            }

                                            // PATCH: Wrap Modal in try-catch
                                            try {
                                                Modal.error({
                                                    title: 'Token Update Failed',
                                                    content: 'Failed to apply the new authentication token to the report. Please refresh the page to reload the report.',
                                                    okText: 'Refresh Page',
                                                    onOk: () => window.location.reload()
                                                });
                                            } catch (modalError) {
                                                console.error('[PowerBI] Modal display failed:', modalError);
                                                if (window.confirm('Token Update Failed\n\nFailed to apply the new authentication token to the report. Click OK to refresh the page.')) {
                                                    window.location.reload();
                                                }
                                            }
                                        }
                                    }
                                } catch (error) {
                                    console.error('[PowerBI] Failed to recover from token expiration:', error);
                                    // Error modal already shown by refreshPowerBIToken
                                }
                            } else {
                                // Not a token error or capacity pause - log for analysis
                                console.warn('[PowerBI] Non-token, non-capacity error detected');
                                console.warn('[PowerBI] Error will be shown by PowerBI SDK default error UI');
                            }
                            } catch (unexpectedError) {
                                // PATCH: Catch any unexpected errors in the error handler itself
                                console.error('[PowerBI] Critical: Unexpected error in PowerBI error handler:', unexpectedError);
                                console.error('[PowerBI] Original event:', event);

                                // Last resort: Show a generic error message
                                try {
                                    Modal.error({
                                        title: 'Report Error',
                                        content: 'An unexpected error occurred while handling a report error. Please refresh the page.',
                                        okText: 'Refresh Page',
                                        onOk: () => window.location.reload()
                                    });
                                } catch (finalModalError) {
                                    console.error('[PowerBI] Even fallback modal failed:', finalModalError);
                                    if (window.confirm('Report Error\n\nAn unexpected error occurred. Click OK to refresh the page.')) {
                                        window.location.reload();
                                    }
                                }
                            }
                        }],
                        ['dataSelected', function (event, entity) {
                            console.log("PowerBIEmbed event [dataSelected]: ", event, entity);  
                            logActivity(event);
                        }],
                        ['buttonClicked', function (event, entity) { 
                            console.log("PowerBIEmbed event [buttonClicked]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['info', function (event, entity) { 
                            console.log("PowerBIEmbed event [info]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['filtersApplied', function (event, entity) { 
                            console.log("PowerBIEmbed event [filtersApplied]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['pageChanged', function (event, entity) { 
                            console.log("PowerBIEmbed event [pageChanged]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['commandTriggered', function (event, entity) { 
                            console.log("PowerBIEmbed event [commandTriggered]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['swipeStart', function (event, entity) { 
                            console.log("PowerBIEmbed event [swipeStart]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['swipeEnd', function (event, entity) { 
                            console.log("PowerBIEmbed event [swipeEnd]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['bookmarkApplied', function (event, entity) { 
                            console.log("PowerBIEmbed event [bookmarkApplied]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['dataHyperlinkClicked', function (event, entity) { 
                            console.log("PowerBIEmbed event [dataHyperlinkClicked]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['visualRendered', function (event, entity) { 
                            console.log("PowerBIEmbed event [visualRendered]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['visualClicked', function (event, entity) { 
                            console.log("PowerBIEmbed event [visualClicked]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['selectionChanged', function (event, entity) { 
                            console.log("PowerBIEmbed event [selectionChanged]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['renderingStarted', function (event, entity) { 
                            console.log("PowerBIEmbed event [renderingStarted]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['blur', function (event, entity) { 
                            console.log("PowerBIEmbed event [blur]: ", event, entity); 
                            logActivity(event);
                        }],
                    ])}
                    cssClassName="w-full h-[80vh] rounded-none"
                    getEmbeddedComponent={async (embeddedReport) => {
                        console.log('Embedded report object:', embeddedReport);
                        // Store report reference for proactive token refresh
                        reportRef.current = embeddedReport;
                        // Expose globally for data dump scripts
                        window.report = embeddedReport;

                        console.log('[AI Summary] Report embedded - cache check will be handled by useEffect');
                    }}
                />
                    </div>

                    {/* Dev Mode: Dataset Dump Button (Blue) - Auto-discovery DAX */}
                    {enableDevDumps && (
                        <Button
                            type="default"
                            icon={<HddOutlined />}
                            onClick={handleDumpDataset}
                            loading={isDumpingDataset}
                            size="large"
                            style={{
                                position: 'fixed',
                                right: '24px',
                                bottom: '168px',
                                zIndex: 9999,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                borderRadius: '50%',
                                width: '56px',
                                height: '56px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#1890ff',
                                borderColor: '#1890ff',
                                color: 'white',
                                pointerEvents: 'auto',
                                cursor: 'pointer'
                            }}
                            title="Dev Mode: Dump Complete Dataset (DAX Auto-Discovery)"
                        />
                    )}

                    {/* Dev Mode: Visual Data Dump Button (Green) - Visual exports */}
                    {enableDevDumps && (
                        <Button
                            type="default"
                            icon={<DatabaseOutlined />}
                            onClick={handleDumpReportData}
                            loading={isDumpingData}
                            size="large"
                            style={{
                                position: 'fixed',
                                right: '24px',
                                bottom: '96px',
                                zIndex: 9999,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                borderRadius: '50%',
                                width: '56px',
                                height: '56px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#52c41a',
                                borderColor: '#52c41a',
                                color: 'white',
                                pointerEvents: 'auto',
                                cursor: 'pointer'
                            }}
                            title="Dev Mode: Dump Visual Data"
                        />
                    )}

                    {/* AI Summary Panel Drawer */}
                    <Drawer
                        title={
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px'
                            }}>
                                <RobotOutlined style={{
                                    fontSize: '18px',
                                    color: '#F16622',
                                    marginTop: '2px'
                                }} />
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px'
                                }}>
                                    <span style={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: '#1a1a1a',
                                        letterSpacing: '-0.01em',
                                        lineHeight: '1.3'
                                    }}>
                                        {currentReportName
                                            ? `Report Insights - ${currentReportName}`
                                            : "Report Insights"}
                                    </span>
                                    {showDashboard ? (
                                        <span style={{
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            color: '#666',
                                            letterSpacing: '-0.005em',
                                            lineHeight: '1.3'
                                        }}>
                                            Dashboard view
                                        </span>
                                    ) : currentPageName ? (
                                        <span style={{
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            color: '#666',
                                            letterSpacing: '-0.005em',
                                            lineHeight: '1.3'
                                        }}>
                                            {currentPageName}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        }
                        extra={
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {drawerExpanded && (
                                    <Tooltip title={showDashboard ? "Show AI Summary" : "Show Executive Dashboard"}>
                                        <Button
                                            type="text"
                                            icon={showDashboard ? <BulbOutlined /> : <DashboardOutlined />}
                                            onClick={() => setShowDashboard(!showDashboard)}
                                            style={{ color: '#F16622', fontSize: '18px' }}
                                        />
                                    </Tooltip>
                                )}
                                <Button
                                    type="text"
                                    icon={drawerExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                                    onClick={() => setDrawerExpanded(!drawerExpanded)}
                                    style={{ color: '#F16622' }}
                                    title={drawerExpanded ? "Collapse to sidebar" : "Expand to dashboard"}
                                />
                            </div>
                        }
                        placement="right"
                        width={drawerExpanded ? '92%' : 820}
                        open={showSummaryPanel}
                        onClose={() => {
                            setShowSummaryPanel(false);
                            setAutoOpenDrawer(false);
                        }}
                        styles={{
                            header: {
                                background: 'linear-gradient(to right, #ffffff 0%, #fafafa 100%)',
                                borderBottom: '2px solid #F16622',
                                padding: '16px 20px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                            },
                            body: { padding: 0 },
                            footer: {
                                background: 'linear-gradient(to top, #f8f8f8 0%, #ffffff 100%)',
                                borderTop: '2px solid #F16622',
                                padding: '14px 16px',
                                boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)'
                            }
                        }}
                        footer={
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <div style={{
                                    fontSize: '11px',
                                    color: '#888',
                                    fontWeight: 500,
                                    letterSpacing: '0.02em'
                                }}>
                                    Powered by <strong style={{
                                        color: '#F16622',
                                        fontWeight: 600
                                    }}>Gemini | BIC Engine</strong>
                                </div>
                                <Button
                                    style={{
                                        border: '2px solid #F16622',
                                        color: '#F16622',
                                        height: '34px',
                                        borderRadius: '17px',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        padding: '0 18px',
                                        background: 'white'
                                    }}
                                    icon={<ReloadOutlined />}
                                    onClick={handleRegenerateSummary}
                                >
                                    Regenerate
                                </Button>
                            </div>
                        }
                    >
                        <AutoSummaryPanel
                            key={`${currentPageName}-${forceSkipCache ? 'regenerate' : 'normal'}`}
                            report={reportRef.current}
                            datasetId={datasetId}
                            organizationId={userDetails?.CustomerID}
                            moduleName={moduleName}
                            embedUrl={embedUrl}
                            accessToken={accessToken}
                            onClose={() => {
                                setShowSummaryPanel(false);
                                setAutoOpenDrawer(false);
                                setForceSkipCache(false); // Reset skipCache when closing
                            }}
                            onRegenerate={handleRegenerateSummary}
                            autoLoad={autoOpenDrawer}
                            skipCache={forceSkipCache}
                            showDashboard={showDashboard}
                            onDashboardChange={setShowDashboard}
                            isExpanded={drawerExpanded}
                        />
                    </Drawer>
                </>
            )}
        </div>
    );
}




export default Report;
